/**
 * SustainabilitySubmissionsPage.jsx — Sürdürülebilirlik Beyanları Yönetim Sayfası
 * Route: /Management/Sustainability
 *
 * Backend hazır değilken localStorage'dan okur (sustainabilityService.js).
 * USE_MOCK_DATA = false → gerçek API'ye geçer.
 */

import React, { useEffect, useState } from "react";
import {
  Card, CardBody, CardHeader,
  Row, Col, Button, Input, Badge, Table,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Alert, Spinner, InputGroup,
} from "reactstrap";
import {
  Eye, Trash2, RefreshCw, Search, Leaf,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import { PageFrame, PageBody } from "../../../Components/Common/EmbeddablePage";
import {
  getSustainabilitySubmissions,
  getSustainabilitySubmission,
  deleteSustainabilitySubmission,
  USE_MOCK_DATA,
} from "../../../services/sustainabilityService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

// ─── Yardımcılar ──────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(currentLocaleTag(), {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const PAGE_SIZE_OPTIONS = [10, 25, 50];

function YesNoBadge({ value }) {
  const { t } = useTranslation("yonetim");
  if (!value) return <span className="text-muted">—</span>;
  return value === "yes"
    ? <Badge color="success">{t("common:evet")}</Badge>
    : <Badge color="secondary">{t("common:hayir")}</Badge>;
}

function DetailRow({ label, value }) {
  const { t } = useTranslation("yonetim");
  if (value === undefined || value === null || value === "") return null;
  return (
    <tr>
      <td className="text-muted small fw-semibold" style={{ width: "45%", whiteSpace: "nowrap" }}>{label}</td>
      <td className="small">
        {Array.isArray(value)
          ? value.length ? value.join(", ") : "—"
          : value === "yes" ? <Badge color="success">{t("common:evet")}</Badge>
          : value === "no"  ? <Badge color="secondary">{t("common:hayir")}</Badge>
          : String(value)}
      </td>
    </tr>
  );
}

// ─── Detay Modal ──────────────────────────────────────────────────────────────
function DetailModal({ id, open, toggle, onDelete }) {
  const { t } = useTranslation("yonetim");
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [delConfirm, setDelConfirm] = useState(false);

  useEffect(() => {
    if (!open || !id) return;
    let cancelled = false;
    async function fetch() {
      setLoading(true);
      setDelConfirm(false);
      try {
        const res = await getSustainabilitySubmission(id);
        if (!cancelled) setData(res);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetch();
    return () => { cancelled = true; };
  }, [id, open]);

  async function handleDelete() {
    await deleteSustainabilitySubmission(id);
    toggle();
    onDelete();
  }

  return (
    <Modal isOpen={open} toggle={toggle} size="lg" scrollable>
      <ModalHeader toggle={toggle}>
        <Leaf size={16} className="me-2 text-success" />
        {t("sustainabilitySubmissions.surdurulebilirlikBeyaniDetayi")}
      </ModalHeader>
      <ModalBody>
        {loading && <div className="text-center py-4"><Spinner color="success" /></div>}
        {!loading && !data && <Alert color="warning">{t("sustainabilitySubmissions.kayitBulunamadi")}</Alert>}
        {!loading && data && (
          <>
            <h6 className="text-primary mb-2">{t("sustainabilitySubmissions.genelBilgiler")}</h6>
            <Table size="sm" bordered className="mb-3">
              <tbody>
                <DetailRow label={t("sustainabilitySubmissions.katilimciFirma")} value={data.exhibitorName} />
                <DetailRow label={t("sustainabilitySubmissions.standNo")} value={data.standNumber} />
                <DetailRow label={t("sustainabilitySubmissions.muteahhitDekorator")} value={data.standContractor} />
                <DetailRow label={t("sustainabilitySubmissions.standBuyuklugu")} value={data.standSize ? data.standSize + " m²" : ""} />
                <DetailRow label={t("sustainabilitySubmissions.kayitTarihi")} value={fmtDate(data.createdAt)} />
              </tbody>
            </Table>

            <h6 className="text-primary mb-2">{t("sustainabilitySubmissions.malzemeler")}</h6>
            <Table size="sm" bordered className="mb-3">
              <tbody>
                <DetailRow label={t("sustainabilitySubmissions.ahsapOrani")} value={data.woodPct} />
                <DetailRow label={t("sustainabilitySubmissions.aluminyum")} value={data.aluminiumPct} />
                <DetailRow label={t("sustainabilitySubmissions.celik")} value={data.steelPct} />
                <DetailRow label={t("sustainabilitySubmissions.diger")} value={data.otherPct} />
                <DetailRow label={t("sustainabilitySubmissions.fscSertifikaliAhsap")} value={data.fscWood} />
                <DetailRow label={t("sustainabilitySubmissions.geriDonusturulmusMalzeme")} value={data.recycledMaterials} />
                {data.recycledMaterials === "yes" && (
                  <DetailRow label={t("sustainabilitySubmissions.geriDonusturulmusMalzemeDetayi")} value={data.recycledDetails} />
                )}
              </tbody>
            </Table>

            <h6 className="text-primary mb-2">{t("sustainabilitySubmissions.grafikZeminYenidenKullanim")}</h6>
            <Table size="sm" bordered className="mb-3">
              <tbody>
                <DetailRow label={t("sustainabilitySubmissions.grafikTuru")} value={data.graphicsType} />
                <DetailRow label={t("sustainabilitySubmissions.grafikYenidenKullanim")} value={data.graphicsReuse} />
                <DetailRow label={t("sustainabilitySubmissions.zeminTuru")} value={data.flooringType} />
                <DetailRow label={t("sustainabilitySubmissions.zeminYenidenKullanilabilir")} value={data.flooringReusable} />
                <DetailRow label={t("sustainabilitySubmissions.standYapisiTuru")}
                  // WAS: "Tek Kullanımlık" / "Kısmen Yeniden Kullanılabilir" / "Tamamen Modüler" (#349: shown, so translated)
                  value={data.standReusability === "single" ? t("sustainabilitySubmissions.tekKullanimlik")
                       : data.standReusability === "partial" ? t("sustainabilitySubmissions.kismenYenidenKullanilabilir")
                       : data.standReusability === "full" ? t("sustainabilitySubmissions.tamamenModuler")
                       : data.standReusability} />
                <DetailRow label={t("sustainabilitySubmissions.dahaOnceKullanildiMi")} value={data.prevUsed} />
                <DetailRow label={t("sustainabilitySubmissions.gelecekteYenidenKullanilacakMi")} value={data.futureReuse} />
                <DetailRow label={t("sustainabilitySubmissions.yenidenKullanimHedefOrani")} value={data.reusePct} />
              </tbody>
            </Table>

            <h6 className="text-primary mb-2">{t("sustainabilitySubmissions.operasyonLojistik")}</h6>
            <Table size="sm" bordered className="mb-3">
              <tbody>
                <DetailRow label={t("sustainabilitySubmissions.ledAydinlatma")} value={data.ledLighting} />
                <DetailRow label={t("sustainabilitySubmissions.dijitalEkranlar")} value={data.digitalDisplays} />
                <DetailRow label={t("sustainabilitySubmissions.kiralikYenidenKullanilabilirMobilya")} value={data.rentalFurniture} />
                <DetailRow label={t("sustainabilitySubmissions.standUretimUlkesi")} value={data.manufacturingCountry} />
                <DetailRow label={t("sustainabilitySubmissions.anaMalzemeMensei")} value={data.materialsOrigin} />
                <DetailRow label={t("sustainabilitySubmissions.turkiyeDeMerkezliMuteahhit")} value={data.localContractor} />
                <DetailRow label={t("sustainabilitySubmissions.yerelTedarikci")} value={data.localSuppliers} />
              </tbody>
            </Table>

            <h6 className="text-primary mb-2">{t("sustainabilitySubmissions.omurSonuEkBilgiler")}</h6>
            <Table size="sm" bordered className="mb-3">
              <tbody>
                <DetailRow label={t("sustainabilitySubmissions.omurSonuPlani")} value={data.endOfLife} />
                {data.additionalInitiatives && (
                  <DetailRow label={t("sustainabilitySubmissions.ekSurdurulebilirlikGirisimleri")} value={data.additionalInitiatives} />
                )}
              </tbody>
            </Table>

            <h6 className="text-primary mb-2">{t("sustainabilitySubmissions.beyan")}</h6>
            <Table size="sm" bordered>
              <tbody>
                <DetailRow label={t("sustainabilitySubmissions.adSoyad")} value={data.declarantName} />
                <DetailRow label={t("sustainabilitySubmissions.firma")} value={data.declarantCompany} />
                <DetailRow label={t("sustainabilitySubmissions.tarih")} value={data.declarantDate} />
                <DetailRow label={t("sustainabilitySubmissions.onaylandi")} value={data.declarationConfirmed ? "yes" : "no"} />
              </tbody>
            </Table>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {!delConfirm ? (
          <>
            <Button color="outline-danger" size="sm" onClick={() => setDelConfirm(true)}>
              <Trash2 size={14} className="me-1" />{" "}{t("common:sil")}
            </Button>
            <Button color="secondary" size="sm" onClick={toggle}>{t("common:kapat")}</Button>
          </>
        ) : (
          <>
            <span className="text-danger small me-2">{t("sustainabilitySubmissions.silmekIstediginizeEminMisiniz")}</span>
            <Button color="danger" size="sm" onClick={handleDelete}>{t("sustainabilitySubmissions.evetSil")}</Button>
            <Button color="outline-secondary" size="sm" onClick={() => setDelConfirm(false)}>{t("sustainabilitySubmissions.vazgec")}</Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}

// ─── Ana Sayfa ────────────────────────────────────────────────────────────────
// embedded: boolean true → sayfa kabuğu kapanır (sekme olarak render edilir)
export default function SustainabilitySubmissionsPage({ embedded = false }) {
  const { t } = useTranslation("yonetim");
  const [items,      setItems]      = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [pageSize,   setPageSize]   = useState(25);
  const [search,     setSearch]     = useState("");
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [detailId,   setDetailId]   = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    document.title = i18n.t("yonetim:sustainabilitySubmissions.surdurulebilirlikBeyanlariFds");
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const res = await getSustainabilitySubmissions({ page, limit: pageSize, search });
        if (!cancelled) {
          setItems(res.data || []);
          setTotal(res.total || 0);
        }
      } catch {
        if (!cancelled) setError(t("sustainabilitySubmissions.verilerYuklenirkenHataOlustu"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [page, pageSize, search, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  function openDetail(id) {
    setDetailId(id);
    setDetailOpen(true);
  }

  function handleSearchKey(e) {
    if (e.key === "Enter") { setPage(1); setRefreshKey(k => k + 1); }
  }

  return (
    <PageFrame embedded={embedded}>
      <PageBody embedded={embedded} title={t("sustainabilitySubmissions.surdurulebilirlikBeyanlari")} pageTitle={t("sustainabilitySubmissions.yonetim")}>

        {/* Başlık Kartı */}
        <Card className="mb-3 border-0 shadow-sm" style={{ background: "linear-gradient(135deg, #198754 0%, #0d6efd 100%)" }}>
          <CardBody className="text-white py-3">
            <div className="d-flex align-items-center gap-2">
              <Leaf size={24} />
              <div>
                <h5 className="mb-0">{t("sustainabilitySubmissions.surdurulebilirlikBeyanlari")}</h5>
                <p className="mb-0 small opacity-75">
                  {t("sustainabilitySubmissions.standSurdurulebilirlikBeyaniBasvurulari")}
                  {USE_MOCK_DATA && (
                    <Badge color="warning" className="ms-2 text-dark">{t("sustainabilitySubmissions.mockLocalStorage")}</Badge>
                  )}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Özet Kartları */}
        <Row className="mb-3">
          <Col md={3}>
            <Card className="border-0 shadow-sm text-center py-2">
              <CardBody className="py-2">
                <div className="h4 text-success mb-0">{total}</div>
                <div className="text-muted small">{t("sustainabilitySubmissions.toplamBeyan")}</div>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm text-center py-2">
              <CardBody className="py-2">
                <div className="h4 text-primary mb-0">
                  {items.filter(i => i.standReusability === "full").length}
                </div>
                <div className="text-muted small">{t("sustainabilitySubmissions.tamModulerStand")}</div>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm text-center py-2">
              <CardBody className="py-2">
                <div className="h4 text-info mb-0">
                  {items.filter(i => i.ledLighting === "yes").length}
                </div>
                <div className="text-muted small">{t("sustainabilitySubmissions.ledAydinlatma")}</div>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm text-center py-2">
              <CardBody className="py-2">
                <div className="h4 text-warning mb-0">
                  {items.filter(i => i.fscWood === "yes").length}
                </div>
                <div className="text-muted small">{t("sustainabilitySubmissions.fscSertifikaliAhsap")}</div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Tablo Kartı */}
        <Card>
          <CardHeader>
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
              <div className="d-flex gap-2 align-items-center">
                <InputGroup style={{ width: 240 }}>
                  <Input
                    placeholder={t("sustainabilitySubmissions.firmaVeyaStandAra")}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={handleSearchKey}
                    bsSize="sm"
                  />
                  <Button size="sm" color="outline-secondary" onClick={() => { setPage(1); setRefreshKey(k => k + 1); }}>
                    <Search size={14} />
                  </Button>
                </InputGroup>
                <Input type="select" bsSize="sm" style={{ width: 80 }}
                  value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                  {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </Input>
              </div>
              <Button size="sm" color="outline-secondary" onClick={() => setRefreshKey(k => k + 1)}>
                <RefreshCw size={14} className="me-1" />{" "}{t("common:yenile")}
              </Button>
            </div>
          </CardHeader>
          <CardBody className="p-0">
            {error && <Alert color="danger" className="m-3">{error}</Alert>}
            {loading ? (
              <div className="text-center py-5"><Spinner color="success" /></div>
            ) : items.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <Leaf size={40} className="mb-2 opacity-50" />
                <p>{t("sustainabilitySubmissions.henuzSurdurulebilirlikBeyaniBulunmuyor")}</p>
                <small>{t("sustainabilitySubmissions.sahadanDoldurulanBeyanlarBuradaListelenecek")}</small>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>{t("sustainabilitySubmissions.firma")}</th>
                      <th>{t("sustainabilitySubmissions.standNo")}</th>
                      <th>{t("sustainabilitySubmissions.standBuyuklugu")}</th>
                      <th>{t("sustainabilitySubmissions.standYapisi")}</th>
                      <th>{t("sustainabilitySubmissions.led")}</th>
                      <th>{t("sustainabilitySubmissions.fscAhsap")}</th>
                      <th>{t("sustainabilitySubmissions.geriDonusum")}</th>
                      <th>{t("sustainabilitySubmissions.tarih")}</th>
                      <th style={{ width: 80 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, i) => (
                      <tr key={item.id}>
                        <td className="text-muted small">{(page - 1) * pageSize + i + 1}</td>
                        <td className="fw-semibold">{item.exhibitorName || "—"}</td>
                        <td><Badge color="secondary">{item.standNumber || "—"}</Badge></td>
                        <td>{item.standSize ? item.standSize + " m²" : "—"}</td>
                        <td>
                          {item.standReusability === "full" && <Badge color="success">{t("sustainabilitySubmissions.tamModuler")}</Badge>}
                          {item.standReusability === "partial" && <Badge color="warning">{t("sustainabilitySubmissions.kismen")}</Badge>}
                          {item.standReusability === "single" && <Badge color="secondary">{t("sustainabilitySubmissions.tekKullanim")}</Badge>}
                          {!item.standReusability && "—"}
                        </td>
                        <td><YesNoBadge value={item.ledLighting} /></td>
                        <td><YesNoBadge value={item.fscWood} /></td>
                        <td>
                          {Array.isArray(item.endOfLife) && item.endOfLife.length > 0
                            ? <Badge color="info">{item.endOfLife[0]}{item.endOfLife.length > 1 ? ` +${item.endOfLife.length - 1}` : ""}</Badge>
                            : "—"}
                        </td>
                        <td className="text-muted small">{fmtDate(item.createdAt)}</td>
                        <td>
                          <Button size="sm" color="outline-primary" onClick={() => openDetail(item.id)}>
                            <Eye size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
          {/* Sayfalama */}
          {!loading && totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top">
              <span className="text-muted small">{total}{" "}{t("sustainabilitySubmissions.kayitSayfa", { totalPages })}</span>
              <div className="d-flex gap-1">
                <Button size="sm" color="outline-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft size={14} />
                </Button>
                <span className="px-2 py-1 small">{page} / {totalPages}</span>
                <Button size="sm" color="outline-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </PageBody>

      <DetailModal
        id={detailId}
        open={detailOpen}
        toggle={() => setDetailOpen(false)}
        onDelete={() => { setRefreshKey(k => k + 1); }}
      />
    </PageFrame>
  );
}


